// -----------------------------------------------------------------------------
// Timestamp helpers shared across the chat surface.
//
// The API serialises `DateTime` columns without a timezone suffix (EF +
// System.Text.Json default). Treat any ISO string from the server as UTC,
// then convert to the user's local zone for display so people on BST/CEST
// don't see times shifted by an hour.
// -----------------------------------------------------------------------------

import { DateTime } from "luxon";

export const parseServerIso = (iso: string): DateTime => {
  const dt = DateTime.fromISO(iso, { zone: "utc" });
  return dt.isValid ? dt.toLocal() : dt;
};

export const formatTime = (iso: string): string =>
  parseServerIso(iso).toFormat("HH:mm");

export const formatDayHeading = (iso: string): string => {
  const dt = parseServerIso(iso);
  const today = DateTime.local().startOf("day");
  const start = dt.startOf("day");
  const diffDays = Math.round(start.diff(today, "days").days);
  if (diffDays === 0) return "Today";
  if (diffDays === -1) return "Yesterday";
  if (diffDays > -7) return dt.toFormat("cccc");
  return dt.toFormat("cccc, d LLL");
};

export const formatRelative = (iso: string | undefined): string => {
  if (!iso) return "";
  const dt = parseServerIso(iso);
  const now = DateTime.local();
  const diffMin = Math.max(0, Math.round(now.diff(dt, "minutes").minutes));
  if (diffMin < 1) return "now";
  if (diffMin < 60) return `${diffMin}m`;
  const diffHr = Math.round(diffMin / 60);
  if (diffHr < 24) return `${diffHr}h`;
  const diffDay = Math.round(diffHr / 24);
  if (diffDay < 7) return `${diffDay}d`;
  return dt.toFormat("d LLL");
};
